import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Save } from 'lucide-react';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal`;
const eur = (c) => (c == null ? '—' : `${(c / 100).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const tone = (p) => (p == null ? 'text-[#5A5F67]' : p < 10 ? 'text-[#E37A72]' : p < 25 ? 'text-[#D4AF37]' : 'text-[#5FC18F]');
const box = 'bg-[#0B0D10] border border-[#1F242C] p-6';

function Bar({ p }) {
  return <div className="h-1.5 w-28 bg-[#1F242C] rounded-full overflow-hidden"><div className={`h-full ${p < 10 ? 'bg-[#E5484D]' : p < 25 ? 'bg-[#D4AF37]' : 'bg-[#2E9E6A]'}`} style={{ width: `${Math.min(100, Math.max(0, p || 0))}%` }} /></div>;
}

function Reminder({ value, headers, onSaved }) {
  const [r, setR] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onSaved((await axios.put(`${API}/wallet-reminder`, { enabled: r.enabled, percent: Number(r.percent) }, { headers })).data); toast.success('Promemoria salvato'); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || 'Valore non valido (1-90%)'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="admin-wallet-reminder">
      <label className="flex items-center gap-2 text-sm text-[#BFBFBF]"><input type="checkbox" checked={r.enabled} onChange={(e) => setR({ ...r, enabled: e.target.checked })} data-testid="admin-wallet-reminder-enabled" />Promemoria al cliente sotto il</label>
      <Input type="number" min={1} max={90} value={r.percent} onChange={(e) => setR({ ...r, percent: e.target.value })} className="h-9 w-20 bg-black border-[#1F242C] text-white" data-testid="admin-wallet-reminder-percent" />
      <span className="text-sm text-[#BFBFBF]">% dell'ultima ricarica</span>
      <button onClick={save} disabled={busy} className="h-9 px-3 text-xs flex items-center gap-1 bg-[#D4AF37] text-black rounded-sm" data-testid="admin-wallet-reminder-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}Salva</button>
      <p className="w-full text-xs text-[#5A5F67]">Soglia unica per tutti i clienti, separata dall'avviso che ogni cliente imposta nel suo plafond. Il promemoria parte una volta e si riattiva con la ricarica successiva.</p>
    </div>
  );
}

function Creditsafe({ headers }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/creditsafe/alerts`, { headers }).then((r) => setD(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const check = async () => {
    setBusy(true);
    try { const r = (await axios.post(`${API}/creditsafe/alerts/check`, {}, { headers })).data; setD(r); toast.success(r.new_warnings ? `Avvisi inviati: ${r.new_warnings}` : 'Nessun nuovo avviso'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Creditsafe non raggiungibile'); } finally { setBusy(false); }
  };
  const lines = (d?.lines || []).filter((l) => l.paid);
  return (
    <div className={box} data-testid="admin-cs-credits">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Crediti Creditsafe</h3>
        <button onClick={check} disabled={busy} className="h-8 px-3 text-xs flex items-center gap-1 border border-[#1F242C] text-[#BFBFBF] hover:text-white rounded-sm" data-testid="admin-cs-credits-check">{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}Verifica ora</button>
      </div>
      <p className="text-xs text-[#5A5F67] mb-4">Controllo automatico ogni giorno: email all'admin sotto il 25% e sotto il 10% dei crediti e 7 giorni prima della scadenza.{d?.checked_at ? ` Ultimo controllo: ${new Date(d.checked_at).toLocaleString('it-IT')}.` : ''}</p>
      {lines.length === 0 ? <p className="text-sm text-[#5A5F67]">Premi «Verifica ora» per leggere il contratto.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-[#1F242C]" data-testid={`admin-cs-line-${i}`}>
              <td className="py-2 pr-3 text-white">{l.country || '—'}</td><td className="py-2 pr-3 text-xs text-[#7A7F87]">{l.template || l.product}</td>
              <td className="py-2 pr-3 font-mono text-xs text-[#BFBFBF]">{l.paid - l.used} / {l.paid}</td>
              <td className={`py-2 pr-3 font-mono text-xs ${tone(l.percent_left)}`}>{l.percent_left}%</td>
              <td className="py-2 text-xs text-[#7A7F87]">{l.expires ? `scade ${new Date(l.expires).toLocaleDateString('it-IT')}` : ''}</td>
            </tr>
          ))}
        </tbody></table></div>
      )}
    </div>
  );
}

export default function AdminWalletsPanel({ headers }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/wallets`, { headers }).then((r) => setD(r.data)).catch(() => toast.error('Plafond non caricati')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div className="space-y-6">
      <div className={box} data-testid="admin-wallets-panel">
        <h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF] mb-4">Plafond dei clienti</h3>
        <Reminder value={d.reminder} headers={headers} onSaved={(reminder) => setD({ ...d, reminder })} />
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#5A5F67] border-b border-[#1F242C]"><th className="py-2 pr-3 font-normal">Cliente</th><th className="py-2 pr-3 font-normal">Residuo</th><th className="py-2 pr-3 font-normal">Ultima ricarica</th><th className="py-2 pr-3 font-normal">% residua</th><th className="py-2 pr-3 font-normal">Soglia cliente</th><th className="py-2 font-normal">Avvisi inviati</th></tr></thead>
          <tbody>{d.clients.map((c) => (
            <tr key={c.id} className="border-b border-[#1F242C]" data-testid={`admin-wallet-row-${c.id}`}>
              <td className="py-2 pr-3"><p className="text-white">{c.company || c.name || c.email}</p><p className="text-xs text-[#5A5F67]">{c.email}</p></td>
              <td className="py-2 pr-3 font-mono text-[#BFBFBF]" data-testid={`admin-wallet-balance-${c.id}`}>{eur(c.balance_cents)}</td>
              <td className="py-2 pr-3 font-mono text-xs text-[#7A7F87]">{eur(c.base_cents)}</td>
              <td className="py-2 pr-3"><div className="flex items-center gap-2"><Bar p={c.percent_left} /><span className={`font-mono text-xs ${tone(c.percent_left)}`} data-testid={`admin-wallet-percent-${c.id}`}>{c.percent_left == null ? '—' : `${c.percent_left}%`}</span></div></td>
              <td className="py-2 pr-3 font-mono text-xs text-[#7A7F87]">{eur(c.client_threshold_cents)}</td>
              <td className="py-2 text-xs text-[#7A7F87]">{[c.client_alert_sent && 'avviso cliente', c.reminder_sent && 'promemoria'].filter(Boolean).join(' · ') || '—'}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      <Creditsafe headers={headers} />
    </div>
  );
}
